import { ONE_SECOND } from '@gem/services/date-service';
import { arrayBufferToHex, encodeRfc3986, hmac } from '@gem/utils';

import { AMZ_SIGN_HEADERS, UNSIGNED_PAYLOAD_HASH, X_AMZ_ALGORITHM } from '../constants';
import { IStorageSessionCredentials } from '../types';
import { getAmzDateStamp } from './getAmzDateStamp';
import { getCanonicalRequestHash } from './getCanonicalRequestHash';
import { getCredentialsScope } from './getCredentialsScope';
import { getRemoteStorageSigningKeyBuffer } from './getRemoteStorageSigningKeyBuffer';

export async function getPresignedFileUrl(
  resourceUrl: string,
  credentials: IStorageSessionCredentials
): Promise<string> {
  const expiresIn = Math.floor(
    (new Date(credentials.expiration).getTime() - new Date().getTime()) / ONE_SECOND
  );

  const { amzDate, dateStamp } = getAmzDateStamp();
  const endpointUrlObject = new URL(credentials.endpoint);
  let canonicalUrlObject = {} as URL;
  try {
    canonicalUrlObject = new URL(resourceUrl);
  } catch {
    return resourceUrl;
  }
  const host = endpointUrlObject.host;

  const canonicalUri = canonicalUrlObject.pathname + canonicalUrlObject.search;

  const scope = getCredentialsScope(credentials, dateStamp);
  const signedHeaders = 'host';

  const params: Record<string, string> = {
    [AMZ_SIGN_HEADERS.XAmzAlgorithm]: X_AMZ_ALGORITHM,
    [AMZ_SIGN_HEADERS.XAmzCredential]: `${credentials.accessKeyId}/${scope}`,
    [AMZ_SIGN_HEADERS.XAmzDate]: amzDate,
    [AMZ_SIGN_HEADERS.XAmzExpires]: String(expiresIn),
    [AMZ_SIGN_HEADERS.XAmzSignedHeaders]: signedHeaders,
    [AMZ_SIGN_HEADERS.XAmzSecurityToken]: credentials.sessionToken,
  };

  const canonicalQueryString = Object.entries(params)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${encodeRfc3986(k)}=${encodeRfc3986(v)}`)
    .join('&');

  const canonicalHeaders = `host:${host}\n`;

  const canonicalRequestHash = await getCanonicalRequestHash({
    method: 'GET',
    canonicalUri,
    canonicalHeaders,
    canonicalQueryString,
    signedHeaders,
    payloadHash: UNSIGNED_PAYLOAD_HASH,
  });

  const stringToSign = [X_AMZ_ALGORITHM, amzDate, scope, canonicalRequestHash].join('\n');

  const keyBuf = await getRemoteStorageSigningKeyBuffer(credentials, dateStamp);
  const signature = arrayBufferToHex(await hmac(keyBuf, stringToSign));

  const signedString = `${canonicalQueryString}&X-Amz-Signature=${signature}`;

  const requestUrl = `${credentials.endpoint}${canonicalUri}`;
  const finalUrl = `${requestUrl}?${signedString}`;
  return finalUrl;
}
